import React, { useState, useEffect } from "react";
import { ScrollView, View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { PreviewSection, MealOptionEditor, ClockInOptions } from "@/components/screens/shared";
import { ShiftTimeInput } from "@/components/experimental/ShiftTimeInput";
import { ShiftType, MealOptionType, SHIFT_VENUE_TYPES, ShiftVenueType, ShiftMetaType } from "@/types/db/shifts";
import { useTheme } from "@/hooks/useTheme";
import Modal from "@/components/Modal";
import { ModalCard } from "@/components/Modals/ModalCard";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { NoteType } from "@/types/db/projects";
import useDateTime from "@/hooks/useDateTime";
import { supabase } from "@/lib/supabase-client";
import Chip from "@/components/ui/Chip";
import { Button } from "@/components/ui/Button";

import { QRCodeModal } from "@/components/Modals/QRCodeModal";
import { useModal } from "@/hooks/useModal";
import { NoteEditor, Note } from "@/components/Editors";
import useAuth from "@/hooks/useAuth";
import { userService } from "@/services/userService";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { ContactInput } from "@/components/inputs/ContactInput";
import { AbilitiesInput } from "@/components/inputs/AbilitiesInput";
import { RestrictionsInput } from "@/components/inputs/RestrictionsInput";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { LookupInput, type LookupInputOption } from "@/components/ui/LookupInput";

interface ShiftOverviewTabProps {
  shift: ShiftType | null;
  loading?: boolean;
}

export function ShiftOverviewTab({ shift, loading = false }: ShiftOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<ShiftType>();
  const { colors } = useTheme();
  const { formatTimestamp } = useDateTime();
  const { openModal, closeModal } = useModal();
  const { user, displayNames } = useAuth();

  // Note editor modal state
  const [noteEditorVisible, setNoteEditorVisible] = useState(false);

  // Current user name for notes
  const [currentUserName, setCurrentUserName] = useState<string>("Unknown");

  // Positions for note display
  const [positions, setPositions] = useState<{ id: string; name: string }[]>([]);

  // Inherited markets from project
  const [projectMarkets, setProjectMarkets] = useState<{ id: string; name: string; fips_code?: string }[]>([]);
  const [projectLocationMarket, setProjectLocationMarket] = useState<{
    id: string;
    name: string;
    fips_code?: string;
  } | null>(null);
  const [shiftLocationMarket, setShiftLocationMarket] = useState<{
    id: string;
    name: string;
    fips_code?: string;
  } | null>(null);

  // Lookup options for location and dress code
  const [locationOptions, setLocationOptions] = useState<LookupInputOption[]>([]);
  const [dressCodeOptions, setDressCodeOptions] = useState<LookupInputOption[]>([]);

  // Use formData when editing, otherwise use shift data
  const displayData = editing ? formData : shift;
  // ── Load location options on mount ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await supabase
          .schema("lists")
          .from("locations")
          .select("id, name, city, state")
          .order("name", { ascending: true });
        if (cancelled) return;
        setLocationOptions(
          (data ?? []).map((l) => ({
            id: l.id,
            name: l.name,
            subtitle: [l.city, l.state].filter(Boolean).join(", ") || undefined,
            icon: "map-marker" as const,
          })),
        );
      } catch (err) {
        console.error("Failed to load location options:", err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Load dress code options on mount ────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await supabase
          .schema("lists")
          .from("dress_codes")
          .select("id, name, description")
          .order("name", { ascending: true });
        if (cancelled) return;
        setDressCodeOptions(
          (data ?? []).map((d) => ({
            id: d.id,
            name: d.name,
            subtitle: d.description || undefined,
            icon: "tshirt-crew" as const,
          })),
        );
      } catch (err) {
        console.error("Failed to load dress code options:", err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Load current user name for notes ────────────────────────
  useEffect(() => {
    const loadCurrentUser = async () => {
      if (!user?.id) return;
      try {
        const userData = await userService.getUserById(user.id);
        if (userData) {
          setCurrentUserName(`${userData.first_name || ""} ${userData.last_name || ""}`.trim() || "Unknown");
        }
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };
    loadCurrentUser();
  }, [user?.id]);

  // ── Fetch positions for note display ────────────────────────
  useEffect(() => {
    const fetchPositions = async () => {
      const { data } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name")
        .order("name", { ascending: true });
      setPositions(data || []);
    };
    fetchPositions();
  }, []);

  // ── Load project's own markets (inherited) ──────────────────
  useEffect(() => {
    const loadProjectMarkets = async () => {
      const projectData = displayData?.project || shift?.project;
      const projectMarketsArray = (projectData as any)?.meta_data?.markets;
      if (!projectMarketsArray || projectMarketsArray.length === 0) {
        setProjectMarkets([]);
        return;
      }
      const { data: markets } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name, fips_code")
        .in("id", projectMarketsArray);
      setProjectMarkets(markets || []);
    };
    loadProjectMarkets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(displayData?.project as any)?.meta_data?.markets, (shift?.project as any)?.meta_data?.markets]);

  // ── Load project's location market (inherited when shift has no location) ──
  useEffect(() => {
    const loadProjectLocationMarket = async () => {
      const projectData = displayData?.project || shift?.project;
      const projectLocationId = (projectData as any)?.location_id;
      if (!projectLocationId) {
        setProjectLocationMarket(null);
        return;
      }
      // Get location's market_id
      const { data: location } = await supabase
        .schema("lists")
        .from("locations")
        .select("market_id")
        .eq("id", projectLocationId)
        .single();
      if (!location?.market_id) {
        setProjectLocationMarket(null);
        return;
      }
      // Get market details
      const { data: market } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name, fips_code")
        .eq("id", location.market_id)
        .maybeSingle();
      setProjectLocationMarket(market || null);
    };
    loadProjectLocationMarket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(displayData?.project as any)?.location_id, (shift?.project as any)?.location_id]);

  // ── Load shift's own location market ────────────────────────
  useEffect(() => {
    const loadShiftLocationMarket = async () => {
      const shiftLocationId = displayData?.location_id || shift?.location_id;
      if (!shiftLocationId) {
        setShiftLocationMarket(null);
        return;
      }
      // Get location's market_id
      const { data: location } = await supabase
        .schema("lists")
        .from("locations")
        .select("market_id")
        .eq("id", shiftLocationId)
        .single();
      if (!location?.market_id) {
        setShiftLocationMarket(null);
        return;
      }
      // Get market details
      const { data: market } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name, fips_code")
        .eq("id", location.market_id)
        .maybeSingle();
      setShiftLocationMarket(market || null);
    };
    loadShiftLocationMarket();
  }, [displayData?.location_id, shift?.location_id]);

  // ── Handlers ────────────────────────────────────────────────

  const handleLocationSelect = async (locationId: string | null) => {
    if (!locationId) {
      updateFormData({
        ...formData,
        location_id: undefined,
        location: undefined,
      });
      return;
    }

    const { data: locationData } = await supabase
      .schema("lists")
      .from("locations")
      .select("id, name, street, city, state, zip, instructions")
      .eq("id", locationId)
      .single();

    if (locationData) {
      updateFormData({
        ...formData,
        location_id: locationId,
        location: {
          id: locationData.id,
          name: locationData.name,
          address: {
            street: locationData.street,
            city: locationData.city,
            state: locationData.state,
            zip: locationData.zip,
          },
        },
      });
    }
  };

  const handleDressCodeSelect = async (dressCodeId: string | null) => {
    if (!dressCodeId) {
      updateFormData({
        ...formData,
        meta_data: {
          ...(formData?.meta_data || {}),
          dress_code_id: undefined,
        },
        dress_code: undefined,
      });
      return;
    }

    const { data: dressCodeData } = await supabase
      .schema("lists")
      .from("dress_codes")
      .select("id, name, description")
      .eq("id", dressCodeId)
      .single();

    if (dressCodeData) {
      updateFormData({
        ...formData,
        meta_data: {
          ...(formData?.meta_data || {}),
          dress_code_id: dressCodeId,
        },
        dress_code: {
          id: dressCodeData.id,
          name: dressCodeData.name,
          description: dressCodeData.description || undefined,
        },
      });
    }
  };

  // Meal options handlers
  const addMealOption = () => {
    const currentOptions = formData?.meta_data?.meal_option || [];
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        meal_option: [...currentOptions, { type: "UNPAID" as const, duration: 30, after: 0 }],
      },
    });
  };

  const removeMealOption = (index: number) => {
    const currentOptions = formData?.meta_data?.meal_option || [];
    const updated = currentOptions.filter((_, i) => i !== index);
    if (updated.length === 0) {
      const { meal_option, ...restMetaData } = formData.meta_data || {};
      updateFormData({
        ...formData,
        meta_data: restMetaData,
      });
    } else {
      updateFormData({
        ...formData,
        meta_data: {
          ...formData.meta_data,
          meal_option: updated,
        },
      });
    }
  };

  const updateMealOption = (index: number, patch: Partial<MealOptionType>) => {
    const currentOptions = formData?.meta_data?.meal_option || [];
    const updated = currentOptions.map((opt, i) => (i === index ? { ...opt, ...patch } : opt));
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        meal_option: updated,
      },
    });
  };

  // ── Helpers ─────────────────────────────────────────────────

  const getVenueType = (): ShiftVenueType | undefined => {
    if (displayData?.meta_data?.venue_type) return displayData.meta_data.venue_type;
    if ((displayData?.project as any)?.meta_data?.venue_type) {
      return (displayData?.project as any).meta_data.venue_type;
    }
    return undefined;
  };

  const isVenueTypeFromProject = () => {
    return !displayData?.meta_data?.venue_type && !!(displayData?.project as any)?.meta_data?.venue_type;
  };

  // Notes helpers with inheritance
  const getShiftNotes = (): NoteType[] => (displayData?.meta_data?.notes as NoteType[]) || [];
  const getProjectNotes = (): NoteType[] => ((displayData?.project as any)?.meta_data?.notes as NoteType[]) || [];
  const hasNotesInheritance = () => getShiftNotes().length === 0 && getProjectNotes().length > 0;

  // Clock-in options helpers with inheritance
  const getClockInOptions = (): Record<string, boolean> => {
    if (displayData?.meta_data?.clock_in_options) {
      return displayData.meta_data.clock_in_options as Record<string, boolean>;
    }
    if ((displayData?.project as any)?.meta_data?.clock_in_options) {
      return (displayData?.project as any).meta_data.clock_in_options as Record<string, boolean>;
    }
    return {};
  };
  const isClockInOptionsFromProject = () =>
    !displayData?.meta_data?.clock_in_options && !!(displayData?.project as any)?.meta_data?.clock_in_options;

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 8, padding: 16 }}>
        {/* Schedule */}
        <ShiftTimeInput
          label="Schedule"
          startTime={displayData?.start_time ? String(displayData.start_time) : null}
          endTime={displayData?.end_time ? String(displayData.end_time) : null}
          editing={editing}
          loading={loading}
          enableHeatmap
          excludeShiftId={shift?.id}
          onChange={(values) => {
            updateFormData({ ...formData, start_time: values.startTime, end_time: values.endTime });
          }}
        />

        {/* Project Details */}
        <PreviewSection title={`${displayNames?.projects?.name || "Project"} Details`}>
          <ClientInput
            value={displayData?.client_id || displayData?.project?.client_id || null}
            state={loading ? "loading" : editing ? "editing" : "idle"}
            label={displayNames?.clients?.name || "Client"}
            onChange={(clientId) =>
              updateFormData({
                ...formData,
                client_id: clientId,
                project_id: undefined,
                project: undefined,
              } as any)
            }
          />
          <ProjectInput
            value={displayData?.project_id || null}
            clientId={displayData?.client_id || displayData?.project?.client_id}
            state={loading ? "loading" : editing ? "editing" : "idle"}
            label={displayNames?.projects?.name || "Project"}
            onChange={(projectId) =>
              updateFormData({
                ...formData,
                project_id: projectId,
              } as any)
            }
          />
        </PreviewSection>

        {/* Location */}
        <LookupInput
          label="Location"
          value={displayData?.location_id || null}
          options={locationOptions}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          icon="map-marker"
          placeholder={
            !displayData?.location && displayData?.project?.location?.name
              ? displayData.project.location.name
              : "No location selected"
          }
          info={
            !displayData?.location_id && displayData?.project?.location
              ? `From ${displayNames?.projects?.name || "Project"}`
              : undefined
          }
          onChange={handleLocationSelect}
        />

        {/* Dress Code */}
        <LookupInput
          label="Dress Code"
          value={(displayData?.meta_data as any)?.dress_code_id || null}
          options={dressCodeOptions}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          icon="tshirt-crew"
          placeholder={
            !displayData?.dress_code && displayData?.project?.dress_code?.name
              ? displayData.project.dress_code.name
              : "No dress code selected"
          }
          info={
            !displayData?.dress_code && displayData?.project?.dress_code
              ? `From ${displayNames?.projects?.name || "Project"}`
              : undefined
          }
          onChange={handleDressCodeSelect}
        />

        {/* Venue Type */}
        <LookupInput
          label="Venue Type"
          value={getVenueType() || null}
          options={SHIFT_VENUE_TYPES.map((vt) => ({ id: vt, name: vt, icon: "home-city" as const }))}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          icon="home-city"
          placeholder="Unspecified"
          info={isVenueTypeFromProject() ? `From ${displayNames?.projects?.name || "Project"}` : undefined}
          onChange={(value) => {
            updateFormData({
              meta_data: {
                ...formData?.meta_data,
                venue_type: value as ShiftVenueType,
              },
            });
          }}
        />

        {/* Clock-In Options */}
        <ClockInOptions
          label="Clock-In Method"
          values={getClockInOptions()}
          editing={editing}
          info={isClockInOptionsFromProject() ? `From ${displayNames?.projects?.name || "Project"}` : undefined}
          trailing={
            shift?.id && getClockInOptions().qr_code ? (
              <Button
                icon="qrcode"
                text="Show QR Code"
                onPress={() => openModal(<QRCodeModal shiftId={shift.id!} onClose={closeModal} />)}
                style={{ paddingHorizontal: 10, paddingVertical: 4, height: 28 }}
                textStyle={{ fontSize: 12 }}
              />
            ) : undefined
          }
          onChange={(newValues) => {
            updateFormData({
              ...formData,
              meta_data: {
                ...(formData?.meta_data || {}),
                clock_in_options: newValues,
              },
            });
          }}
        />

        {/* Onsite Contact */}
        <ContactInput
          value={displayData?.meta_data?.onsite_contact || null}
          clientId={displayData?.client_id || displayData?.project?.client_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label="Onsite Contact"
          placeholder={
            !displayData?.meta_data?.onsite_contact && displayData?.onsite_contact_details?.name
              ? displayData.onsite_contact_details.name
              : "Select contact\u2026"
          }
          info={
            !displayData?.meta_data?.onsite_contact && (displayData?.project as any)?.onsite_contact
              ? `From ${displayNames?.projects?.name || "Project"}`
              : undefined
          }
          onChange={(contactId) =>
            updateFormData({
              ...formData,
              meta_data: {
                ...(formData?.meta_data || {}),
                onsite_contact: contactId || undefined,
              },
            } as any)
          }
        />

        {/* Meal Breaks */}
        <PreviewSection title="Meal Breaks">
          <MealOptionEditor
            items={displayData?.meta_data?.meal_option || []}
            editing={editing}
            onAdd={addMealOption}
            onRemove={removeMealOption}
            onUpdate={updateMealOption}
          />
        </PreviewSection>

        {/* Abilities */}
        <AbilitiesInput
          value={(displayData?.shift_abilities || []).map((sa) => ({ ability: sa.ability }))}
          inherited={[
            ...(displayData?.project?.project_abilities || []).map((pa) => ({
              ability: pa.ability,
              sourceLabel: `From ${displayNames?.projects?.name || "Project"}`,
            })),
            ...(displayData?.project?.client?.client_abilities || []).map((ca) => ({
              ability: ca.ability,
              sourceLabel: `From ${displayNames?.clients?.name || "Client"}`,
            })),
          ]}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(abilities) => updateFormData({ ...formData, shift_abilities: abilities } as any)}
        />

        {/* Restrictions */}
        <RestrictionsInput
          value={(displayData?.shift_restrictions || []).map((sr) => ({ restriction: sr.restriction }))}
          inherited={[
            ...(displayData?.project?.project_restrictions || []).map((pr) => ({
              restriction: pr.restriction,
              sourceLabel: `From ${displayNames?.projects?.name || "Project"}`,
            })),
            ...(displayData?.project?.client?.client_restrictions || []).map((cr) => ({
              restriction: cr.restriction,
              sourceLabel: `From ${displayNames?.clients?.name || "Client"}`,
            })),
          ]}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(restrictions) => updateFormData({ ...formData, shift_restrictions: restrictions } as any)}
        />

        {/* Markets */}
        <MarketsInput
          value={displayData?.meta_data?.markets || []}
          inheritedMarket={
            !displayData?.location_id && !shift?.location_id
              ? projectLocationMarket
                ? { ...projectLocationMarket, label: `From ${displayNames?.projects?.name || "Project"}` }
                : null
              : shiftLocationMarket
                ? { ...shiftLocationMarket, label: "From Location" }
                : null
          }
          inheritedMarkets={projectMarkets.map((m) => ({
            ...m,
            label: `From ${displayNames?.projects?.name || "Project"}`,
          }))}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(ids) =>
            updateFormData({
              ...formData,
              meta_data: {
                ...(formData?.meta_data || {}),
                markets: ids,
              },
            } as any)
          }
        />

        {/* Notes */}
        <PreviewSection
          title="Notes"
          badge={
            hasNotesInheritance() ? (
              <Chip
                text={`From ${displayNames?.projects?.name || "Project"}`}
                style={{ paddingHorizontal: 8, paddingVertical: 2, backgroundColor: colors.cyan?.background }}
              />
            ) : null
          }
          button={
            editing
              ? {
                  iconPosition: "right",
                  icon: "plus",
                  onPress: () => setNoteEditorVisible(true),
                }
              : undefined
          }
          editing={editing}
        >
          {getShiftNotes().length === 0 && getProjectNotes().length === 0 ? (
            <View style={{ padding: 12, alignItems: "center" }}>
              <MaterialCommunityIcons name="note-text-outline" size={24} color={colors.textSecondary} />
              <ThemedText style={{ color: colors.textSecondary, marginTop: 4, fontSize: 12 }}>No notes</ThemedText>
            </View>
          ) : (
            <View style={{ gap: 8 }}>
              {/* Shift-level notes */}
              {getShiftNotes().map((note: NoteType) => (
                <Pressable
                  key={note.id}
                  style={{
                    padding: 10,
                    backgroundColor: colors.backgroundSecondary,
                    borderRadius: 8,
                    borderWidth: StyleSheet.hairlineWidth,
                    borderColor: colors.border,
                  }}
                  onPress={editing ? () => setNoteEditorVisible(true) : undefined}
                >
                  <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                    <ThemedText style={{ fontSize: 12, fontWeight: "600" }}>{note.author_name}</ThemedText>
                    <ThemedText style={{ fontSize: 11, color: colors.textSecondary }}>
                      {formatTimestamp(note.created_at, false)}
                    </ThemedText>
                  </View>
                  <ThemedText style={{ fontSize: 13 }} numberOfLines={2}>
                    {note.message}
                  </ThemedText>
                  {note.position_ids && note.position_ids.length > 0 && (
                    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                      {note.position_ids.length === positions.length && positions.length > 0 ? (
                        <Chip text="All Positions" style={{ paddingHorizontal: 6, paddingVertical: 1 }} />
                      ) : (
                        note.position_ids.map((posId: string) => {
                          const pos = positions.find((p) => p.id === posId);
                          return (
                            <Chip
                              key={posId}
                              text={pos?.name || posId}
                              style={{ paddingHorizontal: 6, paddingVertical: 1 }}
                            />
                          );
                        })
                      )}
                    </View>
                  )}
                </Pressable>
              ))}
              {/* Project-level notes (inherited, read-only) */}
              {getProjectNotes().map((note: NoteType) => (
                <View
                  key={note.id}
                  style={{
                    padding: 10,
                    backgroundColor: colors.backgroundSecondary,
                    borderRadius: 8,
                    borderWidth: StyleSheet.hairlineWidth,
                    borderColor: colors.border,
                    opacity: 0.8,
                  }}
                >
                  <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                      <ThemedText style={{ fontSize: 12, fontWeight: "600" }}>{note.author_name}</ThemedText>
                      <Chip
                        text={`From ${displayNames?.projects?.name || "Project"}`}
                        style={{ paddingHorizontal: 6, paddingVertical: 1, backgroundColor: colors.cyan?.background }}
                      />
                    </View>
                    <ThemedText style={{ fontSize: 11, color: colors.textSecondary }}>
                      {formatTimestamp(note.created_at, false)}
                    </ThemedText>
                  </View>
                  <ThemedText style={{ fontSize: 13 }} numberOfLines={2}>
                    {note.message}
                  </ThemedText>
                  {note.position_ids && note.position_ids.length > 0 && (
                    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                      {note.position_ids.length === positions.length && positions.length > 0 ? (
                        <Chip text="All Positions" style={{ paddingHorizontal: 6, paddingVertical: 1 }} />
                      ) : (
                        note.position_ids.map((posId: string) => {
                          const pos = positions.find((p) => p.id === posId);
                          return (
                            <Chip
                              key={posId}
                              text={pos?.name || posId}
                              style={{ paddingHorizontal: 6, paddingVertical: 1 }}
                            />
                          );
                        })
                      )}
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}
        </PreviewSection>
      </ScrollView>

      {/* Note Editor Modal */}
      {noteEditorVisible && (
        <Modal onUnmount={() => setNoteEditorVisible(false)}>
          <ModalCard>
            <NoteEditor
              notes={(formData?.meta_data?.notes as Note[]) || []}
              inheritedNotes={getProjectNotes() as Note[]}
              currentUserId={user?.id || ""}
              currentUserName={currentUserName}
              onSave={(notes) => {
                updateFormData({
                  ...formData,
                  meta_data: {
                    ...(formData?.meta_data || {}),
                    notes: notes as ShiftMetaType["notes"],
                  },
                });
                setNoteEditorVisible(false);
              }}
              onCancel={() => setNoteEditorVisible(false)}
            />
          </ModalCard>
        </Modal>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
